import React, { useState, useRef, useCallback, useImperativeHandle } from "react"
import { Alert } from "~/ui/primitives"
import QrcodeSvg from "~/ui/qrcode-svg"
import ViewShot from "~/ui/ViewShot"
import Clipboard from "~/ui/clipboard"
import styled from '~/ui/styled'
import useMediaLibrary from "~hooks/useMediaLibrary"
import { Text as TextCore } from "~widgets/text"
import { ShareScreenProps } from "~pages/app-stack/share/types"
import Icon from "~widgets/icon"
import { alertError } from "~utils/alert"

const creditratingBg = require("../../../assets/images/wallet/share-bg.jpeg")
const logo = require("../../../assets/images/app-intro-logo.png")
const url = "https://official.magpiechat.com"

const CreditRating: React.ForwardRefRenderFunction<ViewShot, ShareScreenProps> = (
  _,
  forwardRef,
) => {
  const ref = useRef<ViewShot>()
  useImperativeHandle<ViewShot, ViewShot>(forwardRef, () => ref.current)
  const [loading, setLoading] = useState(false)
  const { saveAssetAsync, requestPermissionsAsyncWithCallback } = useMediaLibrary({ writeOnly: true })

  const getShotUri = useCallback(() => {
    return new Promise<string>((resolve) => {
      ref.current.capture().then((uri) => {
        resolve(uri)
      })
    })
  }, [])

  const saveQrcode = useCallback(() => {
    if (loading) return
    async function save() {
      try {
        setLoading(true)
        const uri = await getShotUri()
        await saveAssetAsync({ uri })
      } catch (e) {
        alertError(e)
      } finally {
        setLoading(false)
      }
    }
    requestPermissionsAsyncWithCallback({
      callback: save,
    })
  }, [loading, getShotUri, requestPermissionsAsyncWithCallback, saveAssetAsync])

  const onCopy = (v: string) => {
    Clipboard.setString(v)
    Alert.alert("复制成功")
  }

  return (
    <Container>
      <ViewShot
        ref={ref}
        style={{ flex: 1 }}
        options={{
          fileName: `magpiechat-share-${Date.now()}`,
          format: "png",
          quality: 1,
        }}
      >
        <Wrap>
          <BgImage src={creditratingBg}></BgImage>
          <Content>
            <LogoView>
              <Logo src={logo} />
            </LogoView>
            <TeBox>
              <Title>红包APP币</Title>
              <Desc>充值无痕 安全无忧</Desc>
            </TeBox>
          </Content>
          <CodeBox>
            <QrcodeSvg value={url} size={91} />
          </CodeBox>
        </Wrap>
      </ViewShot>

      <SceneBox>
        <SceneItem onClick={saveQrcode}>
          <Scene color={"#e5eafe"}>
            <Icon name={"download"} size={24} color={"#598BFC"} />
          </Scene>
          <SceneText>保存图片</SceneText>
        </SceneItem>
        <SceneItem onClick={() => onCopy(url)}>
          <Scene color={"#e7e4fb"}>
            <Icon name={"copy"} size={24} color={"#6C6CEA"} />
          </Scene>
          <SceneText>复制链接</SceneText>
        </SceneItem>
      </SceneBox>
    </Container>
  )
}

const Container = styled.View`
  flex: 1;
`
const Wrap = styled.View`
  flex: 1;
  position: relative;
  padding: 15px 15px 0 15px;
  /* border-radius: 20px; */
  overflow: hidden;
`
const BgImage = styled.ImageBackground`
  flex: 1;
`
const Content = styled.View`
  flex-direction: row;
  align-items: center;
  height: 72px;
  background-color: #fff;
`

const LogoView = styled.View`
  width: 48px;
  height: 48px;
  margin-left: 10px;
  border-radius: 8px;
  overflow: hidden;
`
const Logo = styled.Image`
  width: 48px;
  height: 48px;
`

const TeBox = styled.View`
  margin-left: 6px;
`
const Title = styled(TextCore)`
  font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  color: #121212;
  font-weight: bold;
`
const Desc = styled(TextCore)`
  margin-top: 2px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.infoText};
`

const CodeBox = styled.View`
  position: absolute;
  right: 40px;
  bottom: 15px;
  width: 97px;
  height: 97px;
  padding: 3px;
  background-color: #fff;
`
const SceneBox = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 110px;
  margin-top: 40px;
  padding-bottom: 10px;
  background-color: #fff;
`
const SceneItem = styled.TouchableOpacity`
  align-items: center;
  justify-content: center;
`
const Scene = styled.View<{ color: string }>`
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  margin: 0 30px;
  border-radius: 5px;
  background-color: ${({ color }) => color};
`
const SceneText = styled(TextCore)`
  margin-top: 6px;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.infoText};
`

export default React.forwardRef(CreditRating)
